import classNames from 'classnames'
import {Fragment, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useLocation, useNavigate} from 'react-router-dom'
import {useParams} from 'react-router-dom/'
import {SimpleBlueButton, SmallButton} from '../../components/core/button'
import ButtonLoader from '../../components/core/button/ButtonLoader'
import Checkbox from '../../components/core/checkbox'
import Confirm from '../../components/core/confirm'
import Modal from '../../components/core/modal'
import {PageTitle, ResponsivePageTitle} from '../../components/core/title'
import Footer from '../../components/layouts/footer'
import RenderedForPaid from '../../components/pages/RenderedForPaid'
import RulesetForm from '../../components/pages/rulesetIntervalForm/rulesetForm'
import ListSkeleton from '../../components/pages/rulesetIntervalForm/listView/ListSkeleton'
import os from '../../data/selectOptions/os.json'
import timezone from '../../data/selectOptions/timezone.json'
import {
    ArrowLeftIcon,
    ArrowRightIcon,
    PlusIcon
} from '../../icons'
import {routes} from '../../routes'
import {getAllLimitUsages} from '../../stores/auth/index.js'
import {getServices} from '../../stores/resources'
import {
    addServerResource,
    getServerResource,
    getServerResourceMetrics,
    removeRuleset,
    resetRulesets,
    setRulesets,
    setServerResource,
    setServerResourceId,
    updateServerResource
} from '../../stores/server-monitoring/index.js'
import {colors} from '../../utils/colors'
import {convertToSeconds} from '../../utils/date.js'
import renderSkeletonLoaders from '../../utils/loader'
import {isNewFeatureEnabled} from '../../utils/newFeature.js'
import TextHelper, {trimStringValues} from '../../utils/text'
import MultiForm from '../services/MultiFormService/MultiForm'
import './index.scss'
import {osIcons} from '../../utils/osIcons.js'
import {buildMetricBasedRulesetsPayload} from '../../utils/rulesetMetrics.js'
import useHeaderActions from './UseHeaderActions.jsx'
import modalTypes from '../../components/pages/serverMonitoring/modalTypes.json'
import NavigationHelper from '../../utils/navigation'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations.js'
import {serverMonitoringSchema} from '../../utils/validation.js'
import useDynamicValidation from '../../utils/hooks/useDynamicValidation.js'
import useRulesetUsageLimit from '../../utils/hooks/useRulesetUsageLimit.js'
import {envConfig} from '../../utils/env.js'

const NEXT = 'next'
const PREV = 'prev'
const SERVICE = 'service'
const ENABLE = 'enable'
const DEFAULT_CHART = 'line'
const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
const DEFAULT_OS = os.ubuntu
const UTC_OPTION = {
    value: 'UTC',
    label: 'UTC'
}

const getPaidText = (remainingUsageValue, value, forPaidText) => {
    return remainingUsageValue  > 0 && TextHelper.replaceAllChars(
        TextHelper.replaceAllChars(
            forPaidText,
            ':remaining',
            remainingUsageValue
        ),
        [':limit', ':s'],
        [value, TextHelper.pluralize(value)]
    )
}

const getOsOptions = () => {
    return  Object.values(os).map(osOption => {
        const IconComponent = osIcons[osOption.icon]
        return {
            value: osOption.value,
            type: osOption.type,
            label: (
                <span className='d-flex gap-half'>
                    {IconComponent && <span className='os-icon'><IconComponent width="20" height="20" /></span>}
                    {osOption.label}
                </span>
            )
        }
    })
}
const renderedOsOptions = getOsOptions()
const defaultSelectedOs = renderedOsOptions.find(data => data.value === DEFAULT_OS.value)

const CreateEditServerResource = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const text = useNamespaceTranslations('server-monitoring')
    const rulesetsText = useNamespaceTranslations('rulesets')
    const intervalUnits = useNamespaceTranslations('interval-units')?.interval_units
    const serverMetrics = useNamespaceTranslations('server-metrics')?.server_metrics
    const location = useLocation()
    const formikRef = useRef()
    const {id} = useParams()
    const steps = useMemo(() => Object.values(text.steps), [text.steps])
    const [defaultFormValues, setDefaultFormValues] = useState([])
    const [initialFormValues, setInitialFormValues] = useState(null)
    const [initialValuesMap, setInitialValuesMap] = useState(null)
    const [isNextButtonActive, setIsNextButtonActive] = useState(false)
    const [isFirstLoad, setIsFirstLoad] = useState(true)
    const [stepTransition, setStepTransition] = useState(null)
    const [currentStep, setCurrentStep] = useState(text.steps.form)
    const [visitedSteps, setVisitedSteps] = useState([])
    const [selectedItem, setSelectedItem] = useState(text.steps.selection.items[0])
    const [selectedChart, setSelectedChart] = useState(DEFAULT_CHART)
    const [selectedOs, setSelectedOs] = useState(defaultSelectedOs)
    const [rulesetPendingDelete, setRulesetPendingDelete] = useState(null)

    const createSchema = useCallback(() => serverMonitoringSchema(formikRef.current?.values || {}), [formikRef.current?.values])
    const {validateData, errors, validationSchema} = useDynamicValidation(createSchema)

    const {isButtonLoading,
        isResourceFetching,
        serverResourceId,
        serverResource: updatedResource,
        rulesets,
        isRulesetsFetching} = useSelector(state => state.server_monitoring)
    const services = useSelector((state) => state.resources.services)
    const {isDarkMode, sidebarIsActive} = useSelector(state => state.sidebar)

    const {
        selectedMetricTypes,
        usageLimits,
        handleSelectedMetricTypes,
        renderIntervalSettings,
        state,
        setState,
        isIntervalLimitOverExceeded,
        rulesetFormInitialValues,
        activeModal,
        setActiveModal,
        fetchRulesets
    } = useHeaderActions({isFetching: isFirstLoad, serverResource: updatedResource, showStatusBtns: false})

    const telemetryUrl = `${envConfig.publicApiBaseUrl}server/${id || serverResourceId}/metrics`
    const formType = initialValuesMap?.[selectedItem?.next_step]

    const enabledSteps = useMemo(() => steps.filter(({enabled}) => enabled), [steps])
    const isLimitExceeded = usageLimits.serverResource.usage >= usageLimits.serverResource.value
    const effectiveRulesetUsageLimit = useRulesetUsageLimit(usageLimits.incident_ruleset)
    const paidText = useMemo(
        () => getPaidText(usageLimits.serverResource?.remainingUsageValue, usageLimits.serverResource?.value, text.for_paid),
        [usageLimits.serverResource?.remainingUsageValue,  usageLimits.serverResource?.value]
    )

    const resourcePayload = {
        service_id: formType?.service?.service_id,
        name:  formType?.server_name,
        chart_type_default: selectedChart,
        description: formType?.description,
        timezone: formType?.timezone?.value,
        os: formType?.os?.value,
        metric_types_basic: selectedMetricTypes?.basic?.child_items?.map(childItem => childItem?.value),
        metric_types_advanced: selectedMetricTypes?.advanced?.child_items?.map(childItem => childItem?.value),
        metric_types_complex: selectedMetricTypes?.complex?.child_items?.map(childItem => childItem?.value),
        enabled: formType?.enable,
        interval: state.isAdvancedEnabled && state.intervalValues.interval_value && state.intervalValues.interval_unit ?
            convertToSeconds(state.intervalValues.interval_value, state.intervalValues.interval_unit?.value, intervalUnits) : 0,
        metric_based_rulesets: buildMetricBasedRulesetsPayload(rulesets)
    }

    const isStepCompleted = () => {
        if (currentStep?.type === text.steps.selection.type) {
            return !!selectedItem
        }

        if (currentStep?.type === text.steps.form.type) {
            return (
                formikRef?.current?.values &&
        Object.values(formikRef?.current?.values)?.every((value) => {
            return  typeof value === 'string' ?
                value.trim() !== '' :
                typeof value === 'boolean' ?
                    true :
                    value && !!Object.keys(value)?.length
        })
            )
        }

        if (currentStep?.type === text.steps.chart.type) {
            return !!selectedChart
        }

        if (currentStep?.type === text.steps.metrics.type) {
            return !Object.values(selectedMetricTypes)?.some(metric => (metric?.usage > usageLimits?.[metric?.key]?.value && resourcePayload?.[`metric_types_${metric?.key}`].length > 0)) && !(isIntervalLimitOverExceeded && state.isAdvancedEnabled)
        }

        return true
    }

    const renderServerAgentDetails = () => (
        <div className="selection-details">
            {currentStep?.type === text.steps.completion.type && (
                isFirstLoad ? renderSkeletonLoaders(1, 'circle-loader') : <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="120"
                    height="121"
                    fill="none"
                    viewBox="0 0 120 121"
                >
                    <circle cx="60" cy="60.5" r="42.439" fill="#5D8FFF" />
                    <path
                        stroke="#fff"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="6"
                        d="m46.34 60.5 9.757 9.756 19.512-19.512"
                    />
                </svg>
            )}
            {isFirstLoad ?
                renderSkeletonLoaders(2) :
                <>
                    <span className="selection-title">
                        {currentStep?.step_details.title}
                    </span>
                    <span className="selection-desc">
                        {id ?
                            currentStep?.step_details.description?.replace(':action', text.updated_label) :
                            currentStep?.step_details.description?.replace(':action', text.added_label)}
                    </span>
                </>}
        </div>
    )

    const handleCancel = () => {
        navigate(location.state?.from || NavigationHelper.getLocalizedPath(routes.server_monitoring.paths))
    }

    const handleStepsAction = async(isDone = false, actionType = PREV) => {
        if (isDone) {
            const payload = {...resourcePayload, setup_complete: true}

            const result = await dispatch(updateServerResource(payload, !!id))

            if (result?.success) navigate(NavigationHelper.getLocalizedPath(routes.server_monitoring.paths))
            return
        }

        if (actionType) {
            initialFormValues && setInitialValuesMap({[selectedItem?.next_step]: initialFormValues})
            setStepTransition(actionType)
            return
        }

        setCurrentStep(text.steps.form)
        setVisitedSteps([text.steps.form.key])
        setInitialValuesMap(null)
        dispatch(setServerResourceId(null))
        setSelectedChart(DEFAULT_CHART)
        dispatch(getAllLimitUsages())
        navigate(NavigationHelper.getLocalizedPath(routes.create_server_monitoring.paths))
    }

    const handleSkip = () => {
        const currentIndex = enabledSteps.findIndex((step) => step.key === currentStep?.key)
        const targetIndex = currentIndex + 1
        const targetStep = enabledSteps[targetIndex]
        setCurrentStep({
            ...targetStep,
            active_step: targetStep?.active_step || selectedItem?.next_step
        })
    }

    useEffect(() => {
        dispatch(getServices())
        dispatch(setServerResourceId(null))
        dispatch(resetRulesets())
        dispatch(getAllLimitUsages())
        setIsFirstLoad(false)
        setInitialFormValues(null)
        setInitialValuesMap(null)
    }, [])

    useEffect(() => {
        if (location?.hash === enabledSteps?.[0].url) {
            const updatedFormValues = []
            services?.length > 0 &&
                updatedFormValues.push({
                    name: SERVICE,
                    value: services?.find((item) => item?.service_id === localStorage.getItem('serviceId')),
                    options: services
                })
            updatedFormValues.push(
                {
                    name: 'timezone',
                    value: timezone.timezone_options.find(data => data.value === timeZone),
                    options: timezone.timezone_options
                },
                {
                    name: 'os',
                    value: selectedOs,
                    options: renderedOsOptions
                }
            )

            updatedFormValues.push({
                name: ENABLE,
                value: true
            })
            setDefaultFormValues(updatedFormValues)
        }
    }, [services, location?.hash, timeZone, renderedOsOptions])

    useEffect(() => {
        setIsNextButtonActive(isStepCompleted())
        navigate(`${currentStep?.url}`, {state: location.state})
    }, [currentStep, selectedItem, selectedChart, errors, selectedMetricTypes, state.isAdvancedEnabled])

    useEffect(() => {
        if (currentStep?.type === text.steps.completion.type && !visitedSteps?.includes(currentStep?.key)) {
            setVisitedSteps([...visitedSteps, currentStep?.key])
        }

        if (currentStep?.type === text.steps.metrics.type && selectedOs?.value === os.windows.value) {
            handleSelectedMetricTypes(text.steps.metrics.items.advanced.key, serverMetrics.swap_usage.value, true, true)
        }
    }, [currentStep?.type])

    useEffect(() => {
        if (stepTransition === null) return

        const updateVisitedSteps = () => {
            if (isStepCompleted() && stepTransition === NEXT) {
                setVisitedSteps((prev) =>
                    prev.includes(currentStep?.key) ? prev : [...prev, currentStep?.key]
                )
                return
            }
            setVisitedSteps((prev) =>
                prev.filter((step) => step !== currentStep?.key)
            )
        }

        const updateCurrentStep = (targetIndex) => {
            const targetStep = enabledSteps[targetIndex]
            setCurrentStep({
                ...targetStep,
                active_step: targetStep?.active_step || selectedItem?.next_step
            })
        }

        const handleFormValidation = async() => {
            const {isValid} = await validateData(formikRef.current.values)
            if (!isValid) setStepTransition(null)
            return isValid
        }

        const handleResourceCreate = async() => {
            const result = await dispatch(addServerResource(trimStringValues(resourcePayload)))

            if (!result?.success) {
                setStepTransition(null)
            }

            return result?.success
        }

        const handleRulesetsUpdate = async() => {
            const updatedPayload = {
                ...updatedResource,
                name: resourcePayload?.name,
                chart_type_default: updatedResource?.chart_type_default || selectedChart,
                description: resourcePayload?.description,
                service_id: resourcePayload?.service_id,
                timezone: resourcePayload?.timezone,
                os: resourcePayload?.os,
                metric_types_basic: resourcePayload?.metric_types_basic,
                metric_types_advanced: resourcePayload?.metric_types_advanced,
                metric_types_complex: resourcePayload?.metric_types_complex,
                enabled: resourcePayload?.enabled,
                interval: resourcePayload?.interval,
                metric_based_rulesets: resourcePayload?.metric_based_rulesets
            }

            const result = await dispatch(
                updateServerResource(
                    updatedResource ? trimStringValues(updatedPayload) : trimStringValues(resourcePayload),
                    true,
                    null,
                    false
                )
            )

            if (!result?.success) {
                setStepTransition(null)
            }

            return result?.success
        }

        const handleTransition = async() => {
            const currentIndex = enabledSteps.findIndex(
                (step) => step.key === currentStep?.key
            )
            const targetIndex = stepTransition === NEXT ? currentIndex + 1 : currentIndex - 1

            if (currentStep?.type === text.steps.form.type && stepTransition === NEXT) {
                const isValid = await handleFormValidation()
                if (!isValid) return
            }

            if (currentStep?.type === text.steps.metrics.type && stepTransition === NEXT) {
                if (!serverResourceId) {
                    const isCreated = await handleResourceCreate()
                    if (!isCreated) return
                }
                setState(prev => ({...prev, manualUpdate: true}))
            }

            if (currentStep?.type === text.steps.rulesets.type && stepTransition === NEXT) {
                const isUpdated = await handleRulesetsUpdate()
                if (!isUpdated) return
            }

            if (targetIndex >= 0 && targetIndex < enabledSteps.length) {
                setStepTransition(null)
                updateCurrentStep(targetIndex)
                updateVisitedSteps()
            }
        }

        handleTransition()
    }, [stepTransition])

    useEffect(() => {
        if (id) {
            dispatch(getServerResource(serverMetrics, true, id, navigate, true, false))
            dispatch(setServerResourceId(id))
            return
        }
        dispatch(setServerResource({data: null}))
    }, [id])

    useEffect(() => {
        if (id && updatedResource?.id && currentStep.type === text.steps.rulesets.type) {
            dispatch(getServerResourceMetrics(serverMetrics))
            fetchRulesets()
        }
    }, [id, updatedResource?.id, currentStep.type])

    useEffect(() => {
        if (location?.hash && id) {
            const stepKey = location.hash
            const targetStep = enabledSteps.find((step) => step.url === stepKey)
            setCurrentStep(targetStep)
            const index = enabledSteps.findIndex(step => step.url === stepKey)
            const stepsToVisit = enabledSteps.slice(0, index)
            stepsToVisit.forEach(step => {
                setVisitedSteps(prev => [...prev, step.key])
            })
        }
    }, [location?.pathname, id])

    useEffect(() => {
        if (id) {
            const selectedOsOption = renderedOsOptions.find(data => data.value === updatedResource?.os)
            setInitialValuesMap({[selectedItem?.next_step]: {
                server_name: updatedResource?.name,
                description: updatedResource?.description,
                service: {...services?.find(service => service?.service_id === updatedResource?.service_id), options: services},
                timezone: {...updatedResource?.timezone === UTC_OPTION.value ?
                    UTC_OPTION :
                    timezone.timezone_options.find(data => data.value === updatedResource?.timezone),
                options: timezone.timezone_options},
                os: {...selectedOsOption, options: renderedOsOptions},
                enable: updatedResource?.enabled
            }})
            setSelectedOs(selectedOsOption)
        }
    }, [updatedResource, services, renderedOsOptions])

    useEffect(() => {
        if (!currentStep || !text?.steps) return

        const updatedStep = Object.values(text.steps).find(step => step.key === currentStep.key)

        if (updatedStep && updatedStep.label !== currentStep.label) {
            setCurrentStep({
                ...updatedStep,
                active_step: updatedStep?.active_step || currentStep?.active_step
            })
        }
    }, [text.steps])

    const formData = useMemo(() => {
        if (!selectedItem?.next_step || !currentStep || !text?.steps) return {}

        return {
            [selectedItem.next_step]: currentStep?.[selectedItem?.next_step] || []
        }
    }, [selectedItem?.next_step, currentStep, text.steps])

    const closeModal = () => setActiveModal({type: null, metric_type: null, modal_id: null})
    const showFormViewModal = () => setActiveModal({...activeModal, type: modalTypes.ruleset_form, ruleset: null})

    const showEditModal = (item) => {
        setActiveModal({
            type: modalTypes.ruleset_form,
            metric_type: item.type?.category,
            modal_id: item.type?.key,
            ruleset: item
        })
    }

    const confirmDeleteRuleset = () => {
        dispatch(removeRuleset(rulesets.indexOf(rulesetPendingDelete)))
        setRulesetPendingDelete(null)
    }

    return (
        <main className={classNames('main', {
            dark: isDarkMode
        })} id="server-monitoring">
            {activeModal?.type === modalTypes.ruleset_form &&
            <Modal
                isOpen={activeModal?.type === modalTypes.ruleset_form}
                contentClassName="overflow-auto"
                closeModal={closeModal}
                title='Ruleset details'
                id="rulesets-modal"
            >
                <RulesetForm
                    rulesetFormInitialValues={rulesetFormInitialValues}
                    metricOptions={rulesetFormInitialValues?.type?.options}
                    existingComponent={activeModal?.ruleset?.component?.value}
                    existingRuleset={activeModal?.ruleset}
                    setRulesetValues={(data) => dispatch(setRulesets(data))}
                    closeModal={closeModal}/>
            </Modal>
            }
            <Confirm
                body={rulesetsText.are_you_sure}
                callback={confirmDeleteRuleset}
                isOpen={!!rulesetPendingDelete}
                onClose={() => setRulesetPendingDelete(null)}
            />
            <ResponsivePageTitle />
            <div className="content-title">
                <div className="left-side">
                    <div>
                        <span className={classNames('section', {
                            dark: isDarkMode
                        })}>{text.section}</span>
                        <PageTitle>
                            <h1  className={classNames('page-title', {
                                dark: isDarkMode
                            })}>{text.title}</h1>
                        </PageTitle>
                    </div>
                </div>
                {isFirstLoad ? renderSkeletonLoaders(1, 'btn-skeleton') :
                    text.steps.form.type === currentStep.type &&
                <SmallButton
                    className="cancel-button"
                    onClick={handleCancel}>
                    {text.cancel}
                </SmallButton>}
            </div>
            {isFirstLoad ? renderSkeletonLoaders(1, 'paid-text') : !id && (
                <RenderedForPaid
                    forPaidText={paidText || text.for_paid_general}
                    isExceeded={isLimitExceeded}
                    isTextWithLimits={!!paidText}
                />)}
            <div className={classNames('server-monitoring-container', {
                'metrics-type': currentStep.type === text.steps.metrics.type
            })} >
                <div className={classNames('server-monitoring-content', {
                    dark: isDarkMode
                })}>
                    <div className={classNames('flows-container', {
                        dark: isDarkMode,
                        'd-none': sidebarIsActive
                    })}>
                        <ul className="flows-list">
                            {enabledSteps.map((step, index) => (
                                <Fragment key={index}>
                                    {isFirstLoad ? renderSkeletonLoaders(1, 'flows-item') : <li
                                        className={classNames({
                                            'flows-item': true,
                                            active:
                        step.key === currentStep.key &&
                        currentStep?.type !== text.steps.completion.type,
                                            'visited-step': visitedSteps?.includes(step.key)
                                        })}
                                    >
                                        <Checkbox
                                            name={step.key}
                                            value={index + 1}
                                            id={step.key}
                                            hasValue={
                                                !visitedSteps.includes(step.key) ||
                        (step.key === currentStep.key &&
                          currentStep?.type !== text.steps.completion.type)
                                            }
                                            type="checkbox"
                                            checked={
                                                (step.key !== currentStep.key ||
                          currentStep?.type === text.steps.completion.type) &&
                        visitedSteps.includes(step.key)
                                            }
                                            readOnly
                                        />
                                        <span className="flows-item-text">{step.label}</span>
                                    </li>}
                                    <span className="dots">
                                        {[...Array(5).keys()].map((i) => (
                                            isFirstLoad ? renderSkeletonLoaders(1, 'dots-item') : <span className="dots-item" key={i} />
                                        ))}
                                    </span>
                                </Fragment>
                            ))}
                        </ul>
                    </div>
                    <div className={classNames('multiform-container', {
                        dark: isDarkMode
                    })}>
                        {currentStep.type === text.steps.selection.type && (
                            <>
                                {renderServerAgentDetails()}
                                <MultiForm
                                    monitoringType={text.steps.selection.type}
                                    monitoringTypeData={currentStep?.items}
                                    selectedItem={selectedItem}
                                    onChange={setSelectedItem}
                                    disabled={!id && isLimitExceeded}
                                    isLoading={isResourceFetching}
                                />
                            </>
                        )}
                        {currentStep.type === text.steps.form.type && (
                            <>
                                {renderServerAgentDetails()}
                                <MultiForm
                                    monitoringType={text.steps.form.type}
                                    monitoringTypeData={formData}
                                    formikRef={formikRef}
                                    formSchema={validationSchema}
                                    errors={errors}
                                    initialValuesMap={initialValuesMap}
                                    initialFormValues={initialFormValues}
                                    setInitialValuesMap={setInitialValuesMap}
                                    defaultFormValues={defaultFormValues}
                                    setInitialFormValues={setInitialFormValues}
                                    setIsNextButtonActive={setIsNextButtonActive}
                                    isStepCompleted={isStepCompleted}
                                    setSelectedItem={setSelectedOs}
                                    isLoading={isResourceFetching}
                                    disabled={!id && isLimitExceeded}
                                />
                            </>
                        )}
                        {currentStep.type === text.steps.metrics.type && (
                            <>
                                {renderServerAgentDetails()}
                                <MultiForm
                                    monitoringType={text.steps.metrics.type}
                                    monitoringTypeData={currentStep?.items}
                                    disabled={!id && isLimitExceeded}
                                    selectedMetricTypes={selectedMetricTypes}
                                    onChange={handleSelectedMetricTypes}
                                    selectedItem={selectedOs}
                                    isUsageChartVisible={!id || updatedResource?.enabled}
                                    serverResource={updatedResource}
                                    advancedSettingsContainer={renderIntervalSettings(true)}
                                />
                            </>
                        )}
                        {currentStep.type === text.steps.rulesets.type && isNewFeatureEnabled() && (
                            <>
                                {renderServerAgentDetails()}
                                {isRulesetsFetching ? (
                                    <span className="selection-items ruleset-skeleton-wrapper">
                                        <ListSkeleton isDarkMode={isDarkMode} />
                                    </span>
                                ) : (
                                    <MultiForm
                                        monitoringType={text.steps.rulesets.type}
                                        monitoringTypeData={rulesets}
                                        rulesetUsageLimit={effectiveRulesetUsageLimit}
                                        showEditModal={(item) => showEditModal(item)}
                                        showFormViewModal={showFormViewModal}
                                        onDeleteRuleset={setRulesetPendingDelete}
                                    />
                                )}
                            </>
                        )}
                        {currentStep.type === text.steps.chart.type && (
                            <>
                                {renderServerAgentDetails()}
                                <MultiForm
                                    monitoringType={text.steps.chart.type}
                                    monitoringTypeData={currentStep?.items}
                                    selectedItem={selectedChart}
                                    onChange={setSelectedChart}
                                    disabled={!id && isLimitExceeded}
                                />
                            </>
                        )}
                        {currentStep.type === text.steps.instructions.type && (
                            <>
                                {renderServerAgentDetails()}
                                <MultiForm
                                    monitoringType={text.steps.instructions.type}
                                    monitoringTypeData={currentStep?.items}
                                    telemetryUrl={telemetryUrl}
                                    disabled={!id && isLimitExceeded}
                                    selectedItem={selectedOs}
                                    setSelectedItem={setSelectedOs}
                                    selectedMetricTypes={selectedMetricTypes}
                                />
                            </>
                        )}
                        {currentStep.type === text.steps.completion.type && (
                            <span className="selection-items">
                                {renderServerAgentDetails()}
                            </span>
                        )}
                    </div>
                </div>
                <div className={classNames('action-buttons', {
                    'dark': isDarkMode
                })}>
                    {currentStep?.previous && (isFirstLoad ? renderSkeletonLoaders(1, 'btn-skeleton') :
                        <SimpleBlueButton
                            onClick={() => handleStepsAction()}
                            disabled={currentStep.type === text.steps.selection.type}
                            className="image-btn prev-btn"
                        >
                            <ArrowLeftIcon color={colors.colorMuted} />
                            {text.previous}
                        </SimpleBlueButton>
                    )}
                    <span
                        className={classNames('skip-next-btn', {
                            'completion-btns': currentStep?.type === text.steps.completion.type,
                            'dark': isDarkMode
                        })}
                    >
                        {currentStep?.has_skip && (
                            isFirstLoad ? renderSkeletonLoaders(1, 'btn-skeleton') : <SimpleBlueButton onClick={handleSkip} className='image-btn skip-btn'>{text.skip}</SimpleBlueButton>
                        )}
                        {currentStep?.next_step && (isFirstLoad ? renderSkeletonLoaders(1, 'btn-skeleton') :
                            <SimpleBlueButton
                                onClick={() => handleStepsAction(false, NEXT)}
                                disabled={!isNextButtonActive || (!id && isLimitExceeded) || isButtonLoading ||
                                    (currentStep?.type === text.steps.rulesets.type && isRulesetsFetching)}
                            >
                                {isButtonLoading ? <ButtonLoader /> :
                                    <>
                                        {text.next}
                                        <ArrowRightIcon color={colors.colorWhite} />
                                    </>
                                }
                            </SimpleBlueButton>
                        )}

                        {currentStep?.type === text.steps.completion.type &&
              currentStep?.items?.map((item) => (
                  isFirstLoad ? renderSkeletonLoaders(1, 'btn-skeleton') : <SimpleBlueButton
                      className={`${item?.has_icon ? 'image-btn' : ''}`}
                      onClick={() => handleStepsAction(!item?.has_icon, false)}
                      disabled={isButtonLoading}
                  >
                      {isButtonLoading && !item?.has_icon && <ButtonLoader />}
                      {item.button_text}
                      {item?.has_icon && <PlusIcon stroke={colors.colorMuted} />}
                  </SimpleBlueButton>
              ))}
                    </span>
                </div>
            </div>
            <Footer />
        </main>
    )
}

export default CreateEditServerResource
